import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { roleAllows } from "../api/auth";
import { ErrorState, LoadingState } from "./FeedbackStates";
import { useAuthentication } from "../context/AuthContext";
import type { AppRole } from "../context/AuthContext";

export function RequireAuthentication({ children }: PropsWithChildren) {
  const authentication = useAuthentication();
  const location = useLocation();
  if (authentication.loading) return <LoadingState title="Verifying your MBIP session" />;
  if (authentication.bootstrapFailure !== null) {
    return (
      <ErrorState
        title="Session verification unavailable"
        description="MBIP could not verify this session. This is not treated as a sign-out."
        technicalDetail={authentication.bootstrapFailure}
        actions={<button type="button" onClick={authentication.retryBootstrap}>Retry session verification</button>}
      />
    );
  }
  if (authentication.user === null) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  }
  if (authentication.user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

export function RequireRole({ minimum, children }: PropsWithChildren<{ minimum: AppRole }>) {
  const { user } = useAuthentication();
  if (user === null || !roleAllows(user.role, minimum)) return <Navigate to="/unauthorized" replace />;
  return children;
}
